import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { jmenoZalohy, klicZalohy, type ZalohaPolozka } from '../utils/rozdelOdpovedi';
import { tintZaloha, zalohaBarva, zalohaTextNaPodkladu } from '../theme/zalohaBarva';

interface ZalohaSkupinaProps {
    polozky: ZalohaPolozka[];
    fontSize?: string;
    /** Kolik radku se vejde vcetne radku „+ X dalsich v zaloze“; N <= limit ukaze vsechny. */
    maxViditelnych?: number;
    sx?: SxProps<Theme>;
}

const POREDI_ROLI = ['VD', 'S', 'H'];

const roleZalohy = (p: ZalohaPolozka): string => {
    const kody = (p.odpoved?.funkce ?? p.jinde?.funkce ?? []).map((f) => f.kod);
    return POREDI_ROLI.filter((k) => kody.includes(k as 'VD' | 'S' | 'H')).join(' ');
};

/**
 * Skupina „V zaloze“ na kiosku (spec § 5.2, UX F1). Samostatna komponenta, aby slo rozlozeni
 * zmenit na jednom miste pro vsechny presety. Zamerne bez odpoctu dojezdu a bez „nestiha“
 * (clovek v zaloze uz je ve zbrojnici) a bez barevneho pruhu na leve hrane (jen podklad).
 */
export const ZalohaSkupina: React.FC<ZalohaSkupinaProps> = ({
    polozky, fontSize = '1.3rem', maxViditelnych = 4, sx,
}) => {
    const theme = useTheme();
    if (polozky.length === 0) return null;
    const barva = zalohaBarva(theme);
    const podklad = tintZaloha(theme, 0.14);
    const barvaNaPodkladu = zalohaTextNaPodkladu(theme);
    const viditelne = polozky.length > maxViditelnych ? polozky.slice(0, Math.max(maxViditelnych - 1, 0)) : polozky;
    const prebytek = polozky.length - viditelne.length;

    return (
        <Box data-testid="zaloha-skupina" sx={{ mt: 1, breakInside: 'avoid', ...(sx as object) }}>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: barva, letterSpacing: 0.5, mb: 0.5, lineHeight: 1.2 }}>
                V ZÁLOZE ─ {polozky.length}
            </Typography>
            {viditelne.map((p) => {
                const jmeno = jmenoZalohy(p);
                const role = roleZalohy(p);
                return (
                    <Box
                        key={klicZalohy(p)}
                        data-testid="zaloha-polozka"
                        sx={{
                            px: 1, py: 0.3, mb: 0.5,
                            display: 'flex', alignItems: 'center', gap: 1,
                            borderRadius: 0.5,
                            backgroundColor: podklad,
                            minWidth: 0, overflow: 'hidden',
                        }}
                    >
                        <Typography
                            title={jmeno}
                            sx={{ fontSize, fontWeight: 'bold', flex: '1 1 0', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                            {jmeno}
                        </Typography>
                        {role && (
                            <Typography data-testid="zaloha-role" sx={{ fontSize: '0.9rem', fontWeight: 700, color: 'text.secondary', flexShrink: 0, whiteSpace: 'nowrap' }}>
                                {role}
                            </Typography>
                        )}
                        {p.jinde && (
                            <Typography sx={{ fontSize: '0.9rem', color: barvaNaPodkladu, flexShrink: 0, whiteSpace: 'nowrap' }}>
                                z předchozího poplachu
                            </Typography>
                        )}
                    </Box>
                );
            })}
            {prebytek > 0 && (
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: barva, pl: 1 }}>
                    + {prebytek} dalších v záloze
                </Typography>
            )}
        </Box>
    );
};
